import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import "./Register.css";
import { useDispatch, useSelector } from "react-redux";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ToastService from "@/service/toaster.service";
import customerAuthService from "@/service/auth.service/customer.auth.service";
import { useNavigate } from "react-router-dom";
import { setAuthInfo } from '@/redux/auth/auth.slice';

function CustomerRegister() {
  const [emailError, setEmailError] = useState(""); // New state for email error
  const [firstNameError, setFirstNameError] = useState(""); // State for first name error
  const [lastNameError, setLastNameError] = useState(""); // State for last name error
  const [showPassword, setShowPassword] = useState(false);  // State to control password visibility

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    complexity: false,
    strength: "Weak",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch  =  useDispatch();

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
  
    setFormData({ ...formData, [name]: value });
  
    // Clear field-specific errors on change
    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setEmailError('Please enter a valid email address.');
      } else {
        setEmailError('');
      }
    }
  
    if (name === "firstName") setFirstNameError("");
    if (name === "lastName") setLastNameError("");
  };

  // Validate password
  const validatePassword = (password) => {
    const lengthValid = password.length >= 8;
    const complexityValid =
      /[a-zA-Z]/.test(password) && /\d/.test(password) && /[~!@#$%^&*()_+={}\[\]:;"'<>,.?/\\|-]/.test(password);
    
    let strength = "Weak";
    if (lengthValid && complexityValid) {
      strength = "Strong";
    } else if (lengthValid) {
      strength = "Medium";
    }

    setPasswordValidations({
      length: lengthValid,
      complexity: complexityValid,
      strength: strength,
    });
  };

  const handleContinue = () => {
    // Validate first name
    if (!formData.firstName) {
      setFirstNameError("First Name is required.");
      return false; // Indicate validation failure
    }

    // Validate last name
    if (!formData.lastName) {
      setLastNameError("Last Name is required.");
      return false; // Indicate validation failure
    }

    if(!formData.email) { 
      setEmailError("Email is required.");
      return false; // Indicate validation failure
    }

    // Validate password complexity
    if (!passwordValidations.length || !passwordValidations.complexity) {
      return false; // Indicate validation failure
    }

    return true; // Indicate validation success
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const isValid = handleContinue();
      if (!isValid) {
        setLoading(false);
        return;
      }

      const data = new FormData();
      // Append user data
      data.append("first_name", formData.firstName);
      data.append("last_name", formData.lastName);
      data.append("email", formData.email.toLowerCase());
      data.append("password", formData.password);
      const response = await customerAuthService.registerCustomer(data);
      // if(response.status){
      //   navigate("/customer/auth/verify-email", {
      //     state: {
      //       email: formData.email.toLowerCase(),
      //     },
      //   });
      // }
      if (response?.status) {
        const { access_token, data:customer } = response.data;
      
        // 1. Set access token & role in localStorage
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('role', 'customer');
      
        // 2. Set auth info in Redux
        dispatch(setAuthInfo({
          id: customer._id,
          role: 'customer',
          email: customer.email,
          first_name: customer.first_name,
          last_name: customer.last_name,
          profile_picture: customer.profile_picture,
        }));
      
        // 3. Navigate to upload profile
        navigate('/customer/onboarding/upload-profile-picture', {
          state: { email: customer.email },
        });
      }
    } catch (error) {
      console.error("Error during registration:", error);
      const message =
        error?.response?.data?.data?.error ||
        error?.response?.data?.message ||
        "Registration failed!";
      ToastService.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
        <Paper className="side-card" elevation={5}>
          <Typography variant="h5" className="title" fontWeight={"bold"}>
            Sign up
          </Typography>
          <form className="signup-form" onSubmit={handleSubmit}>
            {/* First Name & Last Name */}
            <div className="name-fields">
              <TextField
                fullWidth
                label="First Name"
                variant="outlined"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={!!firstNameError}
                helperText={firstNameError}
              />
              <TextField
                fullWidth
                label="Last Name"
                variant="outlined"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={!!lastNameError}
                helperText={lastNameError}
              />
            </div>

            {/* Email */}
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              variant="outlined"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={Boolean(emailError)}
              helperText={emailError}
            />

            {/* Password */}
            <TextField
              fullWidth
              margin="normal"
              label="Password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              name="password"
              value={formData.password}
              onChange={(e) => {
                handleChange(e);
                validatePassword(e.target.value);
              }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />

            {/* Password Validation Messages */}
            <Box className="password-validation">
              <Typography variant="body2" className="validationtext">
                <span style={{ color: passwordValidations.length ? "green" : "red" }}>
                  {passwordValidations.length ? "✓" : "✗"}
                </span>{" "}
                At least 8 characters
              </Typography>

              <Typography variant="body2" className="validationtext">
                <span style={{ color: passwordValidations.complexity ? "green" : "red" }}>
                  {passwordValidations.complexity ? "✓" : "✗"}
                </span>{" "}
                Letters, numbers & special characters (e.g. ~!@#$%^&)
              </Typography>

              <Typography variant="body2" className="validationtext">
                <span
                  style={{
                    color:
                      passwordValidations.strength === "Strong"
                        ? "green"
                        : passwordValidations.strength === "Medium"
                        ? "orange"
                        : "red",
                  }}
                >
                  {passwordValidations.strength === "Strong" ? "✓" : "✗"}
                </span>{" "}
                Password Strength: {passwordValidations.strength}
              </Typography>
            </Box>

            {/* Error Message */}
            {error && (
              <Typography color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              className="btn-login"
              sx={{
                mt: 2,
                color: "black",
                fontWeight: "bold",
              }}
              disabled={
                loading ||
                !passwordValidations.length ||
                !passwordValidations.complexity ||
                !!emailError || 
                !formData.email  // to avoid empty email
              }
            >
              {loading ? "Processing..." : "Register"}
            </Button>
          </form>
        </Paper>
      </Box>
    </>
  );
}

export default CustomerRegister;
